/**
 * Locale-aware formatting through Intl, using the active i18n language.
 * Use these instead of `toLocaleString()` / hard-coded "en-US".
 * Components re-render on language change via useTranslation(), so calling
 * these during render keeps output in sync.
 */
import { activeLocale } from "./index";

export const EMPTY_VALUE = "—";

type DateInput = string | number | Date | null | undefined;

/** Date-only strings ("2026-10-04") are read as local dates, not UTC midnight. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d =
    value instanceof Date
      ? value
      : typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(`${value}T00:00:00`)
        : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value: DateInput, options: Intl.DateTimeFormatOptions = { dateStyle: "medium" }): string {
  const d = toDate(value);
  return d ? new Intl.DateTimeFormat(activeLocale(), options).format(d) : EMPTY_VALUE;
}

export function formatDateTime(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" },
): string {
  return formatDate(value, options);
}

export function formatTime(value: DateInput, options: Intl.DateTimeFormatOptions = { timeStyle: "short" }): string {
  return formatDate(value, options);
}

export function formatNumber(value: number | string | null | undefined, options?: Intl.NumberFormatOptions): string {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? new Intl.NumberFormat(activeLocale(), options).format(n) : String(value);
}

/** Currency amount; `currency` is an ISO 4217 code (defaults to USD, the platform's contract currency). */
export function formatCurrency(
  value: number | string | null | undefined,
  currency = "USD",
  options?: Intl.NumberFormatOptions,
): string {
  try {
    return formatNumber(value, { style: "currency", currency, ...options });
  } catch {
    // Unknown currency code from the API: fall back to "1,234 XYZ".
    return `${formatNumber(value, options)} ${currency}`;
  }
}

/** 0–100 score → "42%". */
export function formatPercent(value: number | null | undefined, options?: Intl.NumberFormatOptions): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return formatNumber(value / 100, { style: "percent", maximumFractionDigits: 0, ...options });
}
